import commentsService from '../services/commentsService';
import dataModel from '../models/DataModel';
import OfflineQueue from './queue/OfflineQueue';
import DAO from './DAO.js';
import Metadata from '../helpers/metadata/Metadata';
import StoreItemProcessing from '../helpers/StoreFunctions/StoreItemProcessing';
import { task_attributes } from './TasksDAO';
import { completion_criteria_attributes } from './CompletionCriteriaDAO';

export const comment_root = {
  comment: {},
};

const completionCiteriaSelector = {
  completion_criterion: {
    attachments: { additionalFields: ['upload', 'photo_longitude', 'photo_latitude', 'photo_taken_at'] } ,
    comments: {
      user: { },
    },
  },
};

export default class CommentsDAO extends DAO {

  constructor(store) {
    super(store, 'comment');

    this.OfflineQueue = OfflineQueue;

    this._commentID = -1;
    this.unsetComment = this.unsetComment.bind(this);
    this.clearComments = this.clearComments.bind(this);
    this.handleCompletionStepCommentSend = this.handleCompletionStepCommentSend.bind(this);
  }

  get commentID() {
    return this._commentID--;
  }

  unsetComment(id) {
    StoreItemProcessing.deleteItemInStore(this.itemType, id);
  }

  clearComments() {
    StoreItemProcessing.clearItemsInStore(this.itemType);
  }

  getComment(comment_id, options) {
    return this._getItem(comment_id, commentsService.getComment.bind(this, comment_id, options), options);
  }

  async setCommentInTask(comment, action) {
    const task = await dataModel.tasksDAO.getTask(comment.commentable_id, { recordDescriptor: task_attributes });
    if (task) {
      if (!task.comments){
        task.comments = [];
      }
      if (action == 'delete') {
        task.comments = task.comments ? task.comments.filter(cc => {
          return cc.id != comment.id;
        }) : [];
      }
      else {
        let found = false;
        task.comments = task.comments ? task.comments.map(cc => {
          if (cc.id == comment.id) {
            found = true;
            return comment;
          }
          else {
            return cc;
          }
        }) : [];
        if (!found) {
          task.comments.push(comment);
        }
      }
      dataModel.tasksDAO.normalizeItem(task, { store: true });
      return task;
    }
    else {
      return null;
    }
  }
  
  async setCommentInCompletionCriterion(comment, action) {
    const completionCriterion = await dataModel.completionCriteriaDAO.getCompletionCriterion(comment.commentable_id, { recordDescriptor: completion_criteria_attributes });
    if (completionCriterion) {
      let denormalizedCreationCriterion = dataModel.completionCriteriaDAO.denormalizeItem(completionCriterion, completionCiteriaSelector);
      if (!denormalizedCreationCriterion.comments){
        denormalizedCreationCriterion.comments = [];
      }
      if (action == 'delete') {
        denormalizedCreationCriterion.comments = denormalizedCreationCriterion.comments ? denormalizedCreationCriterion.comments.filter(cc => {
          return cc.id != comment.id;
        }) : [];
      }
      else {
        let found = false;
        denormalizedCreationCriterion.comments = denormalizedCreationCriterion.comments ? denormalizedCreationCriterion.comments.map(cc => {
          if (cc.id == comment.id) {
            found = true;
            return comment;
          }
          else {
            return cc;
          }
        }) : [];
        if (!found) {
          denormalizedCreationCriterion.comments.push(comment);
        }
      }
      return dataModel.completionCriteriaDAO.normalizeItem(denormalizedCreationCriterion, { store: true });
    }
    else {
      return null;
    }
  }
  
  async handleTaskComment(response, action, comment, eventMetadatum, options) {

    if (response.offline) {
      this.markDirty(comment, true);
      OfflineQueue.addQueueItem('comment', action, { id: comment.id, comment: comment, eventMetadatum: eventMetadatum });
      if (!options) {
        options = {};
      }
      options.store = true;
      StoreItemProcessing.normalizeItem(this.itemType, comment, options);
      return comment;
    }
    else {
      StoreItemProcessing.normalizeAndSyncToStore(this.itemType, response);

      let denormalizedComment = this.mapToReturn(this.findItem(response.id));
      if (denormalizedComment) {
        await this.setCommentInTask(denormalizedComment, action);
    
        this.markDirty(denormalizedComment, false);
        return denormalizedComment;
      }
    }
  }

  async handleCompletionCriterionComment(response, action, comment, eventMetadatum, options) {

    if (response.offline) {
      this.markDirty(comment, true);
      OfflineQueue.addQueueItem('comment', action, { id: comment.id, comment: comment, eventMetadatum: eventMetadatum });
      if (!options) {
        options = {};
      }
      options.store = true;
      StoreItemProcessing.normalizeItem(this.itemType, comment, options);
      await this.setCommentInCompletionCriterion(comment, action);
      return comment;
    }
    else {
      const normalizedComment = StoreItemProcessing.normalizeAndSyncToStore(this.itemType, response);
      
      let denormalizedComment = this.mapToReturn(this.findItem(normalizedComment.id));
      this.markDirty(denormalizedComment, false);
      if (denormalizedComment) {
        await this.setCommentInCompletionCriterion(denormalizedComment, action);
      }
      denormalizedComment.user = comment.user;
      return denormalizedComment;
    }
  }

  async handleResponse(response, action, comment, dontQueue, eventMetadatum, options) {
    if (response.error && !response.offline){
      dataModel.displayHTTPError(action, response.error, false);
    }
    else {
      if (comment.commentable_type == 'Task') {
        return this.handleTaskComment(response, action, comment, eventMetadatum, options);
      }
      else if (comment.commentable_type == 'CompletionCriterion') {
        return this.handleCompletionCriterionComment(response, action, comment, eventMetadatum, options);
      }
    }
  }

  async createComment(comment, dontQueue, eventMetadatum, options) {
    if (!eventMetadatum) {
      eventMetadatum = Metadata.generateMetadatum();
    }
    return commentsService.createComment(comment, eventMetadatum, options).then(response => {
      if (response.error && !response.offline) {
        dataModel.displayHTTPError('Creating a comment', response.error, false);
      }
      else {
        if (response.offline) {
          comment.id = this.commentID;
          comment.user = dataModel.usersDAO.currentUser;
          return this.handleResponse(response, 'create', comment, dontQueue, eventMetadatum, options);
        }
        else {
          // if comment in store (may be true if comment added while offline)
          // then remove it
          const commentInStore = this.findItem(comment.id);
          if (commentInStore) {
            StoreItemProcessing.deleteItemInStore(this.itemType, comment.id);
          }
          comment.user = dataModel.usersDAO.currentUser;
          return this.handleResponse(response, 'create', comment, dontQueue, eventMetadatum, options);
        }
      }
    });
  }

  async updateComment(comment, dontQueue, eventMetadatum, options) {
    const normalizedComment = StoreItemProcessing.normalizeItem(this.itemType, comment);
    this.markPending(normalizedComment);
    const denormalizedComment = this.mapToReturn(normalizedComment, options);

    if (dataModel.isOffline) {
      this.handleOfflineResponse(denormalizedComment, 'update', eventMetadatum, options);
      this.throwItemError();
    }
    else {
      this.markPending(normalizedComment);

      if (!eventMetadatum) {
        eventMetadatum = Metadata.generateMetadatum();
      }

      return commentsService.updateComment(comment, eventMetadatum, options).then(response => {
        if (response.error && !response.offline) {
          dataModel.displayHTTPError('Updating a task', response.error, false);
  
        }
        else {
          return this.handleResponse(response, 'update', denormalizedComment, eventMetadatum, options);
        }
      })
        .catch((error) => {
          //for furture TODOS
          console.log(error);
          return denormalizedComment;
        });
    }
  }

  async deleteComment(comment, dontQueue, eventMetadatum) {
    if (!eventMetadatum) {
      eventMetadatum = Metadata.generateMetadatum();
    }
    return commentsService.deleteComment(comment.id, eventMetadatum).then(response => {
      if (response.error && !response.offline) {
        dataModel.displayHTTPError('Deleting a task', response.error, false);

      }
      else {
        return this.handleResponse(response, 'delete', comment, eventMetadatum);
      }
    });
  }

  handleOfflineResponse(comment, activity, eventMetadatum, options) {
    if (!eventMetadatum) {
      eventMetadatum = Metadata.generateMetadatum();
    }
    this.markDirty(comment, true);
    OfflineQueue.addQueueItem('comment', activity, { id: comment.id, comment: comment, eventMetadatum, options });
    return comment;
  }

  async handleCompletionStepCommentSend(user, task, completionStep, type, commentText, analyticsLogger) {
    if (commentText) {
      const comment = {
        caption: type == 'na' ? 'NO PHOTO REQUIRED' : 'REJECTED',
        body: commentText,
        commentable_type: 'CompletionCriterion',
        commentable_id: completionStep.id,
        user_id: user.id,
      };
      await this.createComment(comment, false, null, { recordDescriptor: { comments: {} } });
    }

    if (type == 'reject') {
      if (analyticsLogger && (task.status == 'pending_review' || task.status == 'pending_approval')){
        analyticsLogger.logRejectionEvent();
      }
      const rejectedCompletionStep = await dataModel.completionCriteriaDAO.rejectCompletionCriterion(
        completionStep, 
        commentText ? commentText : 'No reason supplied',
        null, null, null, 
        { recordDescriptor: completionCiteriaSelector }
      );
      return rejectedCompletionStep;
    }
    else {
      return null;
    }
  }

  completeOfflineUpdate(queueItem, dontQueue) {
    const comment = this.findItem(queueItem.itemInfo.id);
    if (!comment) {
      return false;
    }
    switch (queueItem.operationKey) {
    case 'create':
      return this.createComment(comment, queueItem.itemInfo.eventMetadatum);
    case 'update':
      return this.updateComment(comment, queueItem.itemInfo.eventMetadatum);
    case 'delete':
      return this.deleteComment(comment, queueItem.itemInfo.eventMetadatum);
    default:
      break;
    }
  }

  clearState(options) {
    this.clearMyState(this.stateTree.new_comments, options.toIgnore.comments, this.unsetComment, this.clearComments, options);
  }
}



// WEBPACK FOOTER //
// ./src/dao/CommentsDAO.js